import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { Building2, User, FileText, CheckCircle2, Loader2, CreditCard } from "lucide-react";

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";

import {
  contractorEntityFormSchema,
  ContractorEntityFormData,
  DEFAULT_CONTRACTOR_ADDRESS,
  Address,
  ContractorEntity,
  ContractorEntityResponse,
  GetContractorEntityResponse,
} from "@/types/contractor-entity";
import DashboardContainer from "../layout/DashboardContainer";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import ContractorEntityDetails from "./ContractorEntityDetails";
import { OneTimePayment } from "@/components/billing/OneTimePayment";
import BillingAPIService from "@/services/billing-api";

const steps = [
  {
    id: "entity-status",
    title: "Entity Status",
    description: "Do you have an existing UK business entity?",
    icon: Building2,
  },
  {
    id: "entity-details",
    title: "Entity Details",
    description: "Provide your business entity details",
    icon: FileText,
  },
  {
    id: "contact-info",
    title: "Contact Information",
    description: "Your contact details",
    icon: User,
  },
  {
    id: "payment",
    title: "Payment",
    description: "Complete your entity setup payment",
    icon: CreditCard,
  },
  {
    id: "confirmation",
    title: "Confirmation",
    description: "Review and submit your information",
    icon: CheckCircle2,
  },
];

type FormFieldName =
  | "hasExistingEntity"
  | "entityDetails.businessName"
  | "entityDetails.registrationNumber"
  | "entityDetails.proposedBusinessName"
  | "entityDetails.vatRegistered"
  | "entityDetails.vatNumber"
  | "entityDetails.yearlyTurnover"
  | "entityDetails.registeredAddress.street_number"
  | "entityDetails.registeredAddress.route"
  | "entityDetails.registeredAddress.locality"
  | "entityDetails.registeredAddress.state"
  | "entityDetails.registeredAddress.postal_code"
  | "entityDetails.registeredAddress.country"
  | "entityDetails.tradingAddressDifferent"
  | "entityDetails.tradingAddress.street_number"
  | "entityDetails.tradingAddress.route"
  | "entityDetails.tradingAddress.locality"
  | "entityDetails.tradingAddress.state"
  | "entityDetails.tradingAddress.postal_code"
  | "entityDetails.tradingAddress.country"
  | "contactDetails.fullName"
  | "contactDetails.phone";

export default function ContractorEntityWizard() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasEntity, setHasEntity] = useState<boolean | null>(null);
  const [entity, setEntity] = useState<ContractorEntity | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [formKey, setFormKey] = useState(0);
  const [entitySetupPlan, setEntitySetupPlan] = useState<any>(null);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Form data persistence key
  const FORM_DATA_KEY = 'contractor-entity-wizard-data';

  // Save form data to localStorage
  const saveFormData = () => {
    const formData = form.getValues();
    localStorage.setItem(FORM_DATA_KEY, JSON.stringify({
      ...formData,
      currentStep,
      paymentCompleted,
    }));
  };

  // Load form data from localStorage
  const loadFormData = () => {
    try {
      const savedData = localStorage.getItem(FORM_DATA_KEY);
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        console.log("Loading saved form data:", parsedData);
        
        // Restore form values
        form.reset({
          hasExistingEntity: parsedData.hasExistingEntity || false,
          entityDetails: parsedData.entityDetails || {
            businessName: "",
            registrationNumber: "",
            proposedBusinessName: "",
            vatRegistered: false,
            vatNumber: "",
            yearlyTurnover: undefined,
            registeredAddress: { ...DEFAULT_CONTRACTOR_ADDRESS },
            tradingAddressDifferent: false,
            tradingAddress: { ...DEFAULT_CONTRACTOR_ADDRESS },
          },
          contactDetails: parsedData.contactDetails || {
            fullName: "",
            phone: "",
          },
        });
        
        // Restore wizard state
        if (parsedData.currentStep !== undefined) {
          setCurrentStep(parsedData.currentStep);
        }
        if (parsedData.paymentCompleted !== undefined) {
          setPaymentCompleted(parsedData.paymentCompleted);
        }
        
        return true;
      }
    } catch (error) {
      console.error("Error loading form data:", error);
    }
    return false;
  };

  // Clear saved form data
  const clearFormData = () => {
    localStorage.removeItem(FORM_DATA_KEY);
  };

  const fetchEntityDetails = async () => {
    try {
      const response = await ApiService.request<GetContractorEntityResponse>(
        EndpointKeys.GET_CONTRACTOR_ENTITY,
      );
      setHasEntity(response.hasEntity);
      if (response.entity) {
        setEntity(response.entity);
      }
      
      // Fetch entity setup plan for payment
      try {
        const planResponse = await BillingAPIService.getEntitySetupPlan();
        setEntitySetupPlan(planResponse.plan);
      } catch (planError) {
        console.error("Error fetching entity setup plan:", planError);
      }
    } catch (error) {
      console.error("Error fetching entity details:", error);
      toast({
        title: "Error",
        description: "Failed to load entity details. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEntityDetails();

    // Check for payment success URL parameter
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get("payment") === "success" && !paymentCompleted) {
      setPaymentCompleted(true);
      setCurrentStep(4); // Go to confirmation step
      // Clean the URL
      navigate(window.location.pathname, { replace: true });
      // Save form data with payment completion status
      setTimeout(() => saveFormData(), 100);
    }
  }, [toast, paymentCompleted, navigate]); // Add paymentCompleted and navigate to dependencies

  const form = useForm<ContractorEntityFormData>({
    resolver: zodResolver(contractorEntityFormSchema),
    defaultValues: {
      hasExistingEntity: false,
      entityDetails: {
        businessName: "",
        registrationNumber: "",
        proposedBusinessName: "",
        vatRegistered: false,
        vatNumber: "",
        yearlyTurnover: undefined,
        registeredAddress: DEFAULT_CONTRACTOR_ADDRESS,
        tradingAddressDifferent: false,
        tradingAddress: DEFAULT_CONTRACTOR_ADDRESS,
      },
      contactDetails: {
        fullName: "",
        phone: "",
      },
    },
    mode: "onChange",
  });

  // Reset form to clean state when component mounts
  useEffect(() => {
    if (!isInitialized) {
      // Try to load saved form data first
      const hasLoadedData = loadFormData();
      
      if (!hasLoadedData) {
        // Only use clean defaults if no saved data exists
        const cleanDefaults = {
          hasExistingEntity: false,
          entityDetails: {
            businessName: "",
            registrationNumber: "",
            proposedBusinessName: "",
            vatRegistered: false,
            vatNumber: "",
            yearlyTurnover: undefined,
            registeredAddress: { ...DEFAULT_CONTRACTOR_ADDRESS },
            tradingAddressDifferent: false,
            tradingAddress: { ...DEFAULT_CONTRACTOR_ADDRESS },
          },
          contactDetails: {
            fullName: "",
            phone: "",
          },
        };
        
        form.reset(cleanDefaults);
        setCurrentStep(0); // Ensure we start at step 0
      }
      
      setFormKey(prev => prev + 1); // Force form re-render
      setIsInitialized(true);
    }
  }, [isInitialized, form]); // Only run when not initialized

  const onSubmit = async (data: ContractorEntityFormData) => {
    setIsSubmitting(true);
    try {
      const submitData = {
        hasExistingEntity: data.hasExistingEntity,
        ...(data.hasExistingEntity
          ? {
              existingEntityDetails: {
                businessName: data.entityDetails.businessName,
                registrationNumber: data.entityDetails.registrationNumber,
                vatRegistered: data.entityDetails.vatRegistered,
                vatNumber: data.entityDetails.vatRegistered
                  ? data.entityDetails.vatNumber
                  : undefined,
                yearlyTurnover: data.entityDetails.yearlyTurnover,
                registeredAddress: data.entityDetails.registeredAddress,
                tradingAddress: data.entityDetails.tradingAddressDifferent
                  ? data.entityDetails.tradingAddress
                  : undefined,
              },
            }
          : {
              newEntityDetails: {
                proposedBusinessName: data.entityDetails.proposedBusinessName,
                registeredAddress: data.entityDetails.registeredAddress,
                tradingAddress: data.entityDetails.tradingAddressDifferent
                  ? data.entityDetails.tradingAddress
                  : undefined,
                vatRegistration: data.entityDetails.vatRegistered,
                vatNumber: data.entityDetails.vatRegistered
                  ? data.entityDetails.vatNumber
                  : undefined,
                yearlyTurnover: data.entityDetails.yearlyTurnover,
              },
            }),
        contactDetails: data.contactDetails,
      };

      const response = await ApiService.request<ContractorEntityResponse>(
        EndpointKeys.SAVE_CONTRACTOR_ENTITY,
        {
          method: "POST",
          body: submitData,
        },
      );

      toast({
        title: "Success",
        description:
          response.message ||
          "Your entity details have been saved successfully.",
      });
      
      // Clear saved form data on successful submission
      clearFormData();
      
      navigate("/dashboard");
    } catch (error: any) {
      toast({
        title: "Error",
        description:
          error.response?.data?.error ||
          "Failed to save entity details. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatValidationErrors = (errors: any): string[] => {
    const messages: string[] = [];
    
    const extractErrors = (obj: any, prefix = ''): void => {
      for (const [key, value] of Object.entries(obj)) {
        if (value && typeof value === 'object' && 'message' in value) {
          // This is an error object
          const fieldName = prefix ? `${prefix}.${key}` : key;
          let friendlyName = fieldName;
          
          // Convert field names to user-friendly names
                     switch (fieldName) {
             case 'entityDetails.registeredAddress.street_number':
               friendlyName = 'Street Number';
               break;
             case 'entityDetails.registeredAddress.route':
               friendlyName = 'Street Name';
               break;
             case 'entityDetails.registeredAddress.locality':
               friendlyName = 'Town/City';
               break;
             case 'entityDetails.registeredAddress.state':
               friendlyName = 'County';
               break;
             case 'entityDetails.registeredAddress.postal_code':
               friendlyName = 'Postcode';
               break;
             case 'entityDetails.registeredAddress.country':
               friendlyName = 'Country';
               break;
             case 'entityDetails.registeredAddress':
               friendlyName = 'Registered Address';
               break;
             case 'entityDetails.tradingAddress':
               friendlyName = 'Trading Address';
               break;
             case 'entityDetails.businessName':
               friendlyName = 'Business Name';
               break;
             case 'entityDetails.registrationNumber':
               friendlyName = 'Registration Number';
               break;
             case 'entityDetails.proposedBusinessName':
               friendlyName = 'Proposed Business Name';
               break;
             case 'entityDetails.vatNumber':
               friendlyName = 'VAT Number';
               break;
             case 'entityDetails.yearlyTurnover':
               friendlyName = 'Yearly Turnover';
               break;
             case 'contactDetails.fullName':
               friendlyName = 'Full Name';
               break;
             case 'contactDetails.phone':
               friendlyName = 'Phone Number';
               break;
             case 'entityDetails':
               friendlyName = 'Entity Details';
               break;
             default:
               friendlyName = fieldName.replace(/^entityDetails\./, '').replace(/^contactDetails\./, '');
           }
          
          messages.push(`${friendlyName}: ${value.message}`);
        } else if (value && typeof value === 'object') {
          // This is a nested object, recurse
          const newPrefix = prefix ? `${prefix}.${key}` : key;
          extractErrors(value, newPrefix);
        }
      }
    };
    
    extractErrors(errors);
    return messages;
  };

  const handleSubmit = async () => {
    const result = await form.trigger();

    if (result) {
      form.handleSubmit(onSubmit)();
    } else {
      // Show validation errors in toast
      const errors = form.formState.errors;
      const errorMessages = formatValidationErrors(errors);

      toast({
        title: "Please fix the following errors:",
        description: errorMessages.join('\n'),
        variant: "destructive",
      });
    }
  };

  const getStepFields = (step: number): FormFieldName[] => {
    switch (step) {
      case 0:
        return ["hasExistingEntity"];
      case 1:
        const baseFields: FormFieldName[] = [
          "entityDetails.registeredAddress.street_number",
          "entityDetails.registeredAddress.route",
          "entityDetails.registeredAddress.locality",
          "entityDetails.registeredAddress.state",
          "entityDetails.registeredAddress.postal_code",
          "entityDetails.registeredAddress.country",
        ];

        // Add fields based on entity status
        if (form.watch("hasExistingEntity")) {
          baseFields.push("entityDetails.businessName");
          baseFields.push("entityDetails.registrationNumber");
        } else {
          baseFields.push("entityDetails.proposedBusinessName");
        }

        // Add VAT fields if VAT registered
        if (form.watch("entityDetails.vatRegistered")) {
          baseFields.push("entityDetails.vatNumber");
          baseFields.push("entityDetails.yearlyTurnover");
        }

        // Add trading address if different
        if (form.watch("entityDetails.tradingAddressDifferent")) {
          baseFields.push("entityDetails.tradingAddress.street_number");
          baseFields.push("entityDetails.tradingAddress.route");
          baseFields.push("entityDetails.tradingAddress.locality");
          baseFields.push("entityDetails.tradingAddress.state");
          baseFields.push("entityDetails.tradingAddress.postal_code");
          baseFields.push("entityDetails.tradingAddress.country");
        }

        // Also validate the overall address objects for superRefine validation
        baseFields.push("entityDetails.registeredAddress" as FormFieldName);
        if (form.watch("entityDetails.tradingAddressDifferent")) {
          baseFields.push("entityDetails.tradingAddress" as FormFieldName);
        }

        return baseFields;
      case 2:
        return ["contactDetails.fullName", "contactDetails.phone"];
      case 3:
        return []; // Payment step doesn't have form fields
      default:
        return [];
    }
  };

  const validateStep = async () => {
    const currentStepFields = getStepFields(currentStep);
    const isValid = await form.trigger(currentStepFields as any);
    return isValid;
  };

  const nextStep = async () => {
    const isValid = await validateStep();
    if (isValid && currentStep < steps.length - 1) {
      // Save current step data
      const currentData = form.getValues();
      
      // Clear contact details when moving to step 2 (contact info step)
      if (currentStep === 1) {
        form.setValue("contactDetails.fullName", "");
        form.setValue("contactDetails.phone", "");
      }
      
      // Skip payment step if contractor has existing entity or payment is completed
      if (currentStep === 2) {
        const hasExistingEntity = form.getValues("hasExistingEntity");
        if (hasExistingEntity || paymentCompleted) {
          setCurrentStep(currentStep + 2); // Skip payment step (step 3) and go to confirmation (step 4)
        } else {
          setCurrentStep(currentStep + 1); // Go to payment step
        }
      } else {
        setCurrentStep(currentStep + 1);
      }
      
      // Save form data after step change
      setTimeout(() => saveFormData(), 100);
    } else {
      // Show validation errors
      const errors = form.formState.errors;
      const errorMessages = formatValidationErrors(errors);

      toast({
        title: "Please fix the following errors:",
        description: errorMessages.join('\n'),
        variant: "destructive",
      });
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      // Save current step data
      const currentData = form.getValues();
      
      // Skip payment step when going backwards if contractor has existing entity
      if (currentStep === 4) { // If we're on confirmation step (step 4)
        const hasExistingEntity = form.getValues("hasExistingEntity");
        if (hasExistingEntity) {
          setCurrentStep(currentStep - 2); // Skip payment step and go to contact info (step 2)
        } else {
          setCurrentStep(currentStep - 1); // Go to payment step (step 3)
        }
      } else {
        setCurrentStep(currentStep - 1);
      }
      
      // Save form data after step change
      setTimeout(() => saveFormData(), 100);
    }
  };

  if (isLoading) {
    return (
      <DashboardContainer title="Business Details">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </DashboardContainer>
    );
  }

  if (hasEntity && entity) {
    return (
      <ContractorEntityDetails
        entity={entity}
        onUpdate={() => {
          // Refresh entity details
          fetchEntityDetails();
        }}
      />
    );
  }

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <Card>
            <CardHeader>
              <CardTitle>Do you have an existing UK business entity?</CardTitle>
              <CardDescription>
                This helps us determine the next steps for your setup process.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="hasExistingEntity"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4" data-testid="existing-entity-form-item">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        data-testid="existing-entity-checkbox"
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>
                        I have an existing UK business entity
                      </FormLabel>
                      <FormDescription>
                        Select this if you already have a registered business in
                        the UK
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>
        );

      case 1:
        return (
          <Card>
            <CardHeader>
              <CardTitle>
                {form.watch("hasExistingEntity")
                  ? "Existing Entity Details"
                  : "New Entity Setup"}
              </CardTitle>
              <CardDescription>
                {form.watch("hasExistingEntity")
                  ? "Please provide details about your existing business entity."
                  : "Let's help you set up your new business entity."}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {!form.watch("hasExistingEntity") && (
                <Alert>
                  <AlertTitle>Important Information</AlertTitle>
                  <AlertDescription>
                    Setting up a new business entity requires registration with
                    HMRC and potentially Companies House. We'll guide you
                    through the process and help you with the necessary
                    documentation.
                  </AlertDescription>
                </Alert>
              )}

              {form.watch("hasExistingEntity") ? (
                <>
                  <FormField
                    control={form.control}
                    name="entityDetails.businessName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Business Name</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="Enter your registered business name"
                            data-testid="business-name-input"
                          />
                        </FormControl>
                        <FormDescription>
                          Enter your business name exactly as it appears on your
                          registration documents
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="entityDetails.registrationNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Registration Number</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="Enter registration number"
                            data-testid="registration-number-input"
                          />
                        </FormControl>
                        <FormDescription>
                          Enter your business registration number (e.g., Company
                          House number for Limited Companies)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              ) : (
                <FormField
                  control={form.control}
                  name="entityDetails.proposedBusinessName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Proposed Business Name</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Enter your proposed business name"
                          autoComplete="off"
                          data-form-type="other"
                        />
                      </FormControl>
                      <FormDescription>
                        Enter the name you want to register for your business
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="entityDetails.registeredAddress.street_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Street Number</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter street number" data-testid="street-number-input" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="entityDetails.registeredAddress.route"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Street Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter street name" data-testid="street-name-input" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="entityDetails.registeredAddress.locality"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Town/City</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter town or city" data-testid="locality-input" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="entityDetails.registeredAddress.state"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company County</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter county" data-testid="county-input" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="entityDetails.registeredAddress.postal_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Postcode</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter postcode" data-testid="postcode-input" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="entityDetails.registeredAddress.country"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Country</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter country" data-testid="country-input" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="entityDetails.tradingAddressDifferent"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>
                        Trading address is different from registered address
                      </FormLabel>
                      <FormDescription>
                        Check this if your business operates from a different
                        address than the registered address
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              {form.watch("entityDetails.tradingAddressDifferent") && (
                <>
                  <FormField
                    control={form.control}
                    name="entityDetails.tradingAddress.street_number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Street Number</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Enter street number" />
                        </FormControl>
                        <FormDescription>
                          Enter the street number of your trading address
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="entityDetails.tradingAddress.route"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Street Name</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Enter street name" />
                        </FormControl>
                        <FormDescription>
                          Enter the street name of your trading address
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="entityDetails.tradingAddress.locality"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Town/City</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Enter town or city" />
                        </FormControl>
                        <FormDescription>
                          Enter the town or city of your trading address
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="entityDetails.tradingAddress.state"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>County</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Enter county" />
                        </FormControl>
                        <FormDescription>
                          Enter the county of your trading address
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="entityDetails.tradingAddress.postal_code"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Postcode</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Enter postcode" />
                        </FormControl>
                        <FormDescription>
                          Enter the postcode of your trading address
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="entityDetails.tradingAddress.country"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Country</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Enter country" />
                        </FormControl>
                        <FormDescription>
                          Enter the country of your trading address
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}

              <FormField
                control={form.control}
                name="entityDetails.vatRegistered"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4" data-testid="vat-registered-form-item">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        data-testid="vat-registered-checkbox"
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>VAT Registered</FormLabel>
                      <FormDescription>
                        {form.watch("hasExistingEntity")
                          ? "Check this if your business is registered for VAT with HMRC"
                          : "Check this if you want to register for VAT (required if yearly turnover exceeds £90,000)"}
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              {form.watch("entityDetails.vatRegistered") && (
                <>
                  <FormField
                    control={form.control}
                    name="entityDetails.yearlyTurnover"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Yearly Turnover (£)</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="number"
                            placeholder="Enter your yearly turnover"
                            data-testid="yearly-turnover-input"
                            onChange={(e) =>
                              field.onChange(
                                e.target.value
                                  ? Number(e.target.value)
                                  : undefined,
                              )
                            }
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormDescription>
                          Enter your estimated yearly turnover over a 12-month
                          rolling period. VAT registration is required if this
                          exceeds £90,000.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="entityDetails.vatNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>VAT Number</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="Enter VAT number (e.g., GB123456789)"
                            data-testid="vat-number-input"
                          />
                        </FormControl>
                        <FormDescription>
                          {form.watch("hasExistingEntity")
                            ? "Enter your 9-digit VAT registration number starting with 'GB'"
                            : "Leave blank if you don't have a VAT number yet - we'll help you register"}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}
            </CardContent>
          </Card>
        );

      case 2:
        return (
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
              <CardDescription>
                Please provide your contact details for correspondence.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <FormField
                control={form.control}
                name="contactDetails.fullName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Full Name</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Enter your full legal name"
                        autoComplete="off"
                        data-form-type="other"
                        data-testid="full-name-input"
                      />
                    </FormControl>
                    <FormDescription>
                      Enter your full name as it appears on official documents
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="contactDetails.phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone Number</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Enter your business phone number"
                        autoComplete="off"
                        data-form-type="other"
                        data-testid="phone-input"
                      />
                    </FormControl>
                    <FormDescription>
                      Enter a valid UK phone number including country code
                      (e.g., +44...)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>
        );

      case 3:
        return (
          <Card>
            <CardHeader>
              <CardTitle>Payment</CardTitle>
              <CardDescription>
                Please complete your entity setup payment to continue.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {entitySetupPlan && entitySetupPlan.tiers && entitySetupPlan.tiers.length > 0 ? (
                <OneTimePayment
                  planName={entitySetupPlan.name}
                  amount={parseFloat(entitySetupPlan.tiers[0].price)}
                  discountedAmount={parseFloat(entitySetupPlan.tiers[0].price) * 0.6} // 40% discount = 60% of original
                  billingTierId={entitySetupPlan.tiers[0].id}
                  discountCode="ENTITY_SETUP_100"
                  onPaymentSuccess={() => {
                    setPaymentCompleted(true);
                    toast({
                      title: "Payment Successful!",
                      description: "Your entity setup payment has been processed. You can now continue.",
                    });
                    // Move to confirmation step (step 4) after payment
                    setCurrentStep(4);
                    // Save form data with payment completion status
                    setTimeout(() => saveFormData(), 100);
                  }}
                  onPaymentError={(error) => {
                    toast({
                      title: "Payment Error",
                      description: error,
                      variant: "destructive",
                    });
                  }}
                />
              ) : (
                <div className="text-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
                  <p>Loading payment details...</p>
                </div>
              )}
            </CardContent>
          </Card>
        );

      case 4:
        return (
          <Card>
            <CardHeader>
              <CardTitle>Confirmation</CardTitle>
              <CardDescription>
                Please review your information before submitting.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div>
                  <h3 className="font-medium">Entity Status</h3>
                  <p>
                    {form.watch("hasExistingEntity")
                      ? "Has existing UK business entity"
                      : "Setting up new UK business entity"}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-medium">Business Name</h3>
                    <p>
                      {form.watch("hasExistingEntity")
                        ? form.watch("entityDetails.businessName")
                        : form.watch("entityDetails.proposedBusinessName")}
                    </p>
                  </div>
                  {form.watch("hasExistingEntity") &&
                    form.watch("entityDetails.registrationNumber") && (
                      <div>
                        <h3 className="font-medium">Registration Number</h3>
                        <p>{form.watch("entityDetails.registrationNumber")}</p>
                      </div>
                    )}
                </div>

                <div>
                  <h3 className="font-medium">Addresses</h3>
                  <div className="space-y-2">
                    <div>
                      <strong>Registered Address:</strong>
                      <p className="text-sm text-muted-foreground">
                        {[
                          form.watch(
                            "entityDetails.registeredAddress.street_number",
                          ),
                          form.watch("entityDetails.registeredAddress.route"),
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        {form.watch(
                          "entityDetails.registeredAddress.locality",
                        ) && (
                          <>
                            <br />
                            {form.watch(
                              "entityDetails.registeredAddress.locality",
                            )}
                          </>
                        )}
                        {form.watch(
                          "entityDetails.registeredAddress.state",
                        ) && (
                          <>
                            <br />
                            {form.watch(
                              "entityDetails.registeredAddress.state",
                            )}
                          </>
                        )}
                        {form.watch(
                          "entityDetails.registeredAddress.postal_code",
                        ) && (
                          <>
                            <br />
                            {form.watch(
                              "entityDetails.registeredAddress.postal_code",
                            )}
                          </>
                        )}
                        {form.watch(
                          "entityDetails.registeredAddress.country",
                        ) && (
                          <>
                            <br />
                            {form.watch(
                              "entityDetails.registeredAddress.country",
                            )}
                          </>
                        )}
                      </p>
                    </div>
                    {form.watch("entityDetails.tradingAddressDifferent") &&
                      form.watch("entityDetails.tradingAddress") && (
                        <div>
                          <strong>Trading Address:</strong>
                          <p className="text-sm text-muted-foreground">
                            {[
                              form.watch(
                                "entityDetails.tradingAddress.street_number",
                              ),
                              form.watch("entityDetails.tradingAddress.route"),
                            ]
                              .filter(Boolean)
                              .join(" ")}
                            {form.watch(
                              "entityDetails.tradingAddress.locality",
                            ) && (
                              <>
                                <br />
                                {form.watch(
                                  "entityDetails.tradingAddress.locality",
                                )}
                              </>
                            )}
                            {form.watch(
                              "entityDetails.tradingAddress.state",
                            ) && (
                              <>
                                <br />
                                {form.watch(
                                  "entityDetails.tradingAddress.state",
                                )}
                              </>
                            )}
                            {form.watch(
                              "entityDetails.tradingAddress.postal_code",
                            ) && (
                              <>
                                <br />
                                {form.watch(
                                  "entityDetails.tradingAddress.postal_code",
                                )}
                              </>
                            )}
                            {form.watch(
                              "entityDetails.tradingAddress.country",
                            ) && (
                              <>
                                <br />
                                {form.watch(
                                  "entityDetails.tradingAddress.country",
                                )}
                              </>
                            )}
                          </p>
                        </div>
                      )}
                  </div>
                </div>

                {form.watch("entityDetails.vatRegistered") && (
                  <div>
                    <h3 className="font-medium">VAT Information</h3>
                    <p>
                      <strong>VAT Registered:</strong> Yes
                    </p>
                    {form.watch("entityDetails.yearlyTurnover") && (
                      <p>
                        <strong>Yearly Turnover:</strong> £
                        {form
                          .watch("entityDetails.yearlyTurnover")
                          ?.toLocaleString()}
                      </p>
                    )}
                    {form.watch("entityDetails.vatNumber") && (
                      <p>
                        <strong>VAT Number:</strong>{" "}
                        {form.watch("entityDetails.vatNumber")}
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <h3 className="font-medium">Contact Information</h3>
                  <p>Name: {form.watch("contactDetails.fullName")}</p>
                  <p>Phone: {form.watch("contactDetails.phone")}</p>
                </div>

                {/* Payment Status Section */}
                <div>
                  <h3 className="font-medium">Payment Status</h3>
                  {form.watch("hasExistingEntity") ? (
                    <div className="flex items-center space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <p className="text-green-600">
                        Payment not required - You have an existing business entity
                      </p>
                    </div>
                  ) : paymentCompleted ? (
                    <div className="flex items-center space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <p className="text-green-600">Payment completed successfully</p>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <CreditCard className="h-4 w-4 text-orange-600" />
                      <p className="text-orange-600">Payment required for entity setup</p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <DashboardContainer title="Business Details">
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => {
            const hasExistingEntity = form.watch("hasExistingEntity");
            const isPaymentStep = index === 3; // Payment step is index 3
            const isPaymentSkipped = hasExistingEntity && isPaymentStep;
            
            return (
              <div
                key={step.id}
                className={`flex items-center ${
                  index < steps.length - 1 ? "flex-1" : ""
                }`}
              >
                <div
                  className={`flex items-center justify-center w-8 h-8 rounded-full ${
                    isPaymentSkipped
                      ? "bg-muted text-muted-foreground opacity-50"
                      : index <= currentStep
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted"
                  }`}
                >
                  <step.icon className="w-4 h-4" />
                </div>
                {isPaymentSkipped && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    (Skipped)
                  </span>
                )}
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-2 ${
                      isPaymentSkipped
                        ? "bg-muted opacity-50"
                        : index < currentStep
                        ? "bg-primary"
                        : "bg-muted"
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            {currentStep !== 3 && renderStep()}

            {currentStep !== 3 && (
              <div className="flex justify-between">
                <Button
                  type="button"
                  variant="outline"
                  onClick={prevStep}
                  disabled={currentStep === 0 || isSubmitting}
                >
                  Previous
                </Button>
                {currentStep === steps.length - 1 ? (
                  <Button
                    type="button"
                    disabled={isSubmitting}
                    onClick={handleSubmit}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      "Submit"
                    )}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={nextStep}
                    disabled={isSubmitting}
                  >
                    Next
                  </Button>
                )}
              </div>
            )}
          </form>
        </Form>

        {/* Payment step rendered outside of wizard form to avoid nesting */}
        {currentStep === 3 && (
          <div className="space-y-8">
            {renderStep()}
            <div className="flex justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={prevStep}
                disabled={isSubmitting}
              >
                Previous
              </Button>
              {/* No Next button - payment component handles navigation */}
            </div>
          </div>
        )}
      </div>
    </DashboardContainer>
  );
}
